import React, { useEffect, useState } from "react";
import Banner from "./sections/PageBanner";
import PageHeader from "./sections/PageHeader";
import Footer from "./sections/PageFooter";
import axios from "../../utils/axios";
import Cases from "../Cases";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import html2pdf from "html2pdf.js";
import ReactGA from "react-ga4";

ReactGA.initialize("G-FMCF2WQ3F6");

/* ── Helpers ────────────────────────────────────────────────────────── */
const stripHtml = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText;
};

/**
 * Content stored from external sources (e.g. migrated MongoDB documents)
 * sometimes contains literal backslash-n sequences (the two chars \ and n)
 * rather than real Unicode newlines.  In HTML, real newlines between block
 * elements are whitespace-collapsed and invisible; literal \n text nodes are
 * not — they render as the visible characters "\n".
 * This function strips both forms so they never appear in the rendered output.
 */
const cleanContent = (html: string): string =>
  html
    .replace(/\\n/g, '')   // literal backslash-n  (\ + n, 2 chars)
    .replace(/\\r/g, '');  // literal backslash-r  (\ + r, 2 chars)

const handleDownloadPDF = (title: string, content: string) => {
  const container = document.createElement("div");
  container.innerHTML = `
    <div style="font-family:'Arial',sans-serif;padding:20px;font-size:14px;">
      <h1 style="font-size:20px;font-weight:bold;">${title}</h1>
      <div>${cleanContent(content)}</div>
    </div>`;
  html2pdf()
    .set({ margin: 0.5, filename: `${title}.pdf`, html2canvas: { scale: 2 }, jsPDF: { unit: "in", format: "letter", orientation: "portrait" } })
    .from(container)
    .save();
};

const handleDownloadDocx = async (caseTitle: string, caseContent: string) => {
  const doc = new Document({
    sections: [{
      properties: {},
      children: [
        new Paragraph({ children: [new TextRun({ text: caseTitle, bold: true, size: 28 })] }),
        new Paragraph({ text: stripHtml(caseContent), spacing: { before: 200 } }),
      ],
    }],
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${caseTitle}.docx`);
};

/* ── Page hero ──────────────────────────────────────────────────────── */
function PageHero() {
  return (
    <div
      className="relative w-full overflow-hidden flex flex-col justify-end"
      style={{ height: '46vh', minHeight: 320, background: '#052e16' }}
    >
      <img
        src="/images/v1/img12.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover select-none"
        style={{ opacity: 0.28 }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#052e16]/20 via-transparent to-[#052e16]/95 pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-12">
        <span className="flex items-center gap-3 mb-4">
          <span className="h-px w-8 bg-green-400" />
          <span className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            Legal Intelligence
          </span>
        </span>
        <h1
          className="text-5xl lg:text-6xl font-bold text-white leading-tight"
          style={{ fontFamily: 'var(--font-display)', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
        >
          Case <span className="text-green-400">Precedents</span>
        </h1>
        <p
          className="mt-4 text-white/65 text-base max-w-xl leading-relaxed"
          style={{ fontFamily: 'var(--font-body)', textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          A curated library of landmark wildlife crime rulings and legal precedents powering
          prosecution strategy across Africa.
        </p>
      </div>
    </div>
  );
}

/* ── Download button ────────────────────────────────────────────────── */
function DownloadMenu({ title, content }: { title: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium hover:border-green-300 hover:text-green-700 transition-all"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Download
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-10 cursor-default bg-transparent border-0 p-0"
            onClick={() => setOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
          />
          <div className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden min-w-[130px]">
            <button
              onClick={() => { handleDownloadPDF(title, content); setOpen(false); }}
              className="w-full px-4 py-2.5 text-left text-xs text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Download PDF
            </button>
            <button
              onClick={() => { handleDownloadDocx(title, content); setOpen(false); }}
              className="w-full px-4 py-2.5 text-left text-xs text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors border-t border-gray-100"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Download DOCX
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Case card ──────────────────────────────────────────────────────── */
function CaseCard({ item, index }: { item: any; index: number }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-green-300 hover:shadow-md hover:shadow-green-100/50 transition-all duration-300">
      {/* Header */}
      <div className="flex items-start gap-4 p-6">
        <span
          className="shrink-0 w-8 h-8 rounded-lg bg-[#052e16] text-green-400 flex items-center justify-center text-xs font-bold"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex-1 min-w-0">
          <h3
            className="text-gray-900 font-semibold text-base leading-snug mb-2"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {item?.title}
          </h3>
          {/* Preview snippet */}
          {!expanded && item?.content && (
            <div
              className="text-gray-500 text-sm leading-relaxed line-clamp-2"
              style={{ fontFamily: 'var(--font-body)' }}
              dangerouslySetInnerHTML={{ __html: cleanContent(item.content) }}
            />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div
          className="px-6 pb-5 pt-1 border-t border-gray-100 prose prose-sm prose-green max-w-none text-gray-600"
          style={{ fontFamily: 'var(--font-body)', fontSize: 13 }}
          dangerouslySetInnerHTML={{ __html: cleanContent(item.content) }}
        />
      )}

      {/* Footer actions */}
      <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-green-700 text-xs font-semibold hover:text-green-600 transition-colors"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          {expanded ? (
            <>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
              Collapse
            </>
          ) : (
            <>
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="w-3.5 h-3.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              Read full case
            </>
          )}
        </button>
        <div className="flex-1" />
        <DownloadMenu title={item?.title ?? ''} content={cleanContent(item?.content ?? '')} />
      </div>
    </div>
  );
}

/* ── Precedents list ────────────────────────────────────────────────── */
function Precedents() {
  const [cases, setCases]   = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]  = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get("/precedents/all")
      .then((res) => { if (res.status === 200) setCases(res.data.data); })
      .catch((_err) => { /* handled by loading state */ })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Cases />;

  const filtered = cases.filter((c) =>
    !search || c?.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-full bg-[#fafaf7] py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">

        {/* Search + count */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-10">
          <div className="relative flex-1 max-w-sm">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search cases…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all"
              style={{ fontFamily: 'var(--font-body)' }}
            />
          </div>
          <p className="text-gray-400 text-sm shrink-0" style={{ fontFamily: 'var(--font-body)' }}>
            {filtered.length} {filtered.length === 1 ? 'case' : 'cases'} found
          </p>
        </div>

        {/* Case cards */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
              {cases.length === 0 ? 'No case precedents available yet.' : 'No cases match your search.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filtered.map((item, i) => (
              <CaseCard key={i} item={item} index={i} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
function PrecedentsPage() {
  ReactGA.send({ hitType: "pageview", page: "/case-precedents", title: "Case Precedents" });
  return (
    <>
      <Banner />
      <PageHeader />
      <PageHero />
      <Precedents />
      <Footer />
    </>
  );
}

export default PrecedentsPage;
